import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { BUSINESS_ID } from '../utils/seo';
/**
 * Replaces the former Ceramic Coating page.
 *
 * Mike's Pro Detail applies a sprayed-on hybrid ceramic product, not a
 * professional multi-year ceramic coating. This page describes only what is
 * actually offered and deliberately avoids the words "ceramic coating",
 * "professional ceramic coating", and "permanent", along with any multi-year
 * durability claim.
 */
export function HybridCeramicSprayPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Hybrid Ceramic Spray Protection',
    serviceType: 'Hybrid Ceramic Spray Paint Protection',
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: ['Camarillo', 'Ventura', 'Thousand Oaks', 'Ventura County'],
    description: 'Sprayed-on hybrid ceramic protection that adds gloss and helps paint shed water and dirt, applied on-site anywhere in Ventura County.'
  };
  const faqs = [{
    question: 'What is hybrid ceramic spray?',
    answer: 'Hybrid ceramic spray is a sprayed-on paint protection product that combines traditional wax or sealant technology with ceramic (silica-based) ingredients. It adds gloss and helps your paint shed water and dirt, and it is applied by hand as part of a detail.'
  }, {
    question: 'Is hybrid ceramic spray the same as a ceramic coating?',
    answer: 'No. A professional ceramic coating is a separate, multi-step service that bonds a durable layer to your clear coat and typically requires paint correction first. Hybrid ceramic spray is a sprayed-on protectant applied during a detail. Mike\u2019s Pro Detail offers hybrid ceramic spray, not professional ceramic coating.'
  }, {
    question: 'How long does hybrid ceramic spray last?',
    answer: 'Longevity depends on the product, the weather, and how often the vehicle is washed and where it is parked. It outlasts a traditional carnauba wax, but it is not a permanent or multi-year coating. Call (818) 381-9905 and we will give you a realistic expectation for your vehicle.'
  }, {
    question: 'Is hybrid ceramic spray included in any package?',
    answer: 'Yes. Hybrid ceramic protection is included in our Wash & Wax package and our Full Detail. It can also be added to other services — call (818) 381-9905 for a quote.'
  }, {
    question: 'Can you apply it at my home or office?',
    answer: 'Yes. Like every service we offer, hybrid ceramic spray is applied on-site at your home or office anywhere in Ventura County. We bring our own water and detailing equipment and require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Hybrid Ceramic Spray',
    item: 'https://www.fulldetailpro.com/services/hybrid-ceramic-spray'
  }];
  return <ServicePageLayout title="Hybrid Ceramic Spray Protection in Ventura County" subtitle="Added gloss and water-beading protection for your paint, sprayed on by hand as part of your mobile detail." metaDescription="Hybrid ceramic spray protection in Ventura County — Camarillo, Ventura & Thousand Oaks. Added gloss and water beading, applied at your door. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-930" data-mp="w29i6z1d6dwo">
      <p data-id="element-931" data-mp="i4tcxqut0q6i">
        Hybrid ceramic spray is the paint protection we apply at the end of a{' '}
        <Link to="/services/exterior-detailing" data-id="element-932" data-mp="3fx4vtsqzd5w">mobile exterior detail</Link>.
        It is a sprayed-on product that blends traditional sealant technology
        with silica-based ceramic ingredients, and it leaves your paint glossier
        and better able to shed water, dust, and road grime. Mike's Pro Detail
        applies it on-site at your home or office throughout Ventura County —
        Camarillo, Ventura, Thousand Oaks, and the surrounding
        communities.
      </p>

      <h2 data-id="element-933" data-mp="3ijov80wyjyew">Hybrid Ceramic Spray vs. Ceramic Coating</h2>
      <p data-id="element-934" data-mp="3ijymrsxnidmp">
        These two are often confused, so it is worth being clear about what you
        are getting. A professional ceramic coating is a separate, highly
        technical service: the paint has to be corrected first, the coating
        bonds to the clear coat, and it needs a controlled environment and a
        long cure time.{' '}
        <strong data-id="element-935" data-mp="2zt73nznv38v4">
          Mike's Pro Detail offers hybrid ceramic spray, not professional ceramic
          coating.
        </strong>{' '}
        Hybrid ceramic spray is applied by hand during your detail, costs a
        fraction as much, and delivers real gloss and water beading without the
        multi-day process. We would rather tell you exactly what you are buying
        than sell you a term that does not match the work.
      </p>

      <h2 data-id="element-936" data-mp="p1e1iivquq2n">What Hybrid Ceramic Spray Does for Your Paint</h2>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-937" data-mp="26z0xavdnjs57">
        <li className="flex items-start" data-id="element-938" data-mp="26y941r7j26i3">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-939" data-mp="2bx2f7czcdlf3" />
          <span data-id="element-940" data-mp="2bv98xysn537m">
            <strong data-id="element-941" data-mp="2gokhq5h0ju26">Water Beading:</strong> Rain and wash water bead up and roll
            off instead of sheeting across the panel, carrying loose dirt with
            them.
          </span>
        </li>
        <li className="flex items-start" data-id="element-942" data-mp="1nzwtc10ywoke">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-943" data-mp="1o17tlqv7fe6r" />
          <span data-id="element-944" data-mp="1sxyauhiqwftr">
            <strong data-id="element-945" data-mp="1szj5e7527yrm">Added Gloss:</strong> The finish looks deeper and more
            reflective, especially on darker paint, immediately after
            application.
          </span>
        </li>
        <li className="flex items-start" data-id="element-946" data-mp="2207jf1ugdz4i">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-947" data-mp="21x1v3otnf19u" />
          <span data-id="element-948" data-mp="38eyshstqcq9m">
            <strong data-id="element-949" data-mp="38hkvrg9l8jm1">Easier Washing:</strong> Dust, pollen, and light road grime
            release more easily, so routine washes take less effort and are
            gentler on the paint.
          </span>
        </li>
        <li className="flex items-start" data-id="element-950" data-mp="19aqigs66fnor">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-951" data-mp="199oytzb3hsvf" />
          <span data-id="element-952" data-mp="1nb1ba0r1rcck">
            <strong data-id="element-953" data-mp="1n9ggqb4qftep">A Barrier Against the Elements:</strong> Adds a sacrificial
            layer between your clear coat and the Southern California sun,
            coastal salt air, and everyday contamination.
          </span>
        </li>
      </ul>

      <h2 data-id="element-954" data-mp="ujhltz3ux224">How We Apply It</h2>
      <p data-id="element-955" data-mp="pn9cj2ccc32v">
        Protection only performs as well as the surface underneath it, so the
        paint is washed and decontaminated before anything is applied. We hand
        wash the vehicle, clay bar the paint to lift bonded contamination, dry
        it thoroughly, then spray and buff the hybrid ceramic product panel by
        panel. Because we are fully mobile, all of this happens in your
        driveway or parking space.
      </p>

      <h2 data-id="element-956" data-mp="6vq94ueemn0b">Where Hybrid Ceramic Spray Is Included</h2>
      <p data-id="element-957" data-mp="2ij3975duigm7">
        Hybrid ceramic protection is already part of our{' '}
        <Link to="/full-detail" data-id="element-958" data-mp="2ihs3h00uaysg">Full Detail</Link> and our Wash
        &amp; Wax package, and it can be added to most other services. We also
        offer a hybrid ceramic glass treatment and a longer-duration hybrid
        ceramic treatment — call{' '}
        <a href="tel:8183819905" data-id="element-959" data-mp="2dlbhabd7aui0">(818) 381-9905</a> and we will recommend the
        right option for your vehicle and quote it honestly.
      </p>

      <h2 data-id="element-960" data-mp="3697bn9fasabm">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-961" data-mp="3684l52lw09w8">
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-962" data-mp="2s9xv28n6je74">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-963" data-mp="1gwc90wcm9spt">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-964" data-mp="28hayi9wy6bzr">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/service-areas/camarillo" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-965" data-mp="1ylhbtxpil249">
          Mobile Detailing in Camarillo &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}